import * as Y from 'yjs';
import {yDocToProsemirrorJSON} from 'y-prosemirror';
import {getMapPreview} from '../components/map/mapPreviewsYMap';

function toSerializable(value, seen = new WeakSet()) {
    if (value === null || value === undefined) return value;
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value;

    if (Array.isArray(value)) {
        return value.map((item) => toSerializable(item, seen));
    }

    if (typeof value === 'object') {
        if (seen.has(value)) return null;
        seen.add(value);

        if (typeof value.toJSON === 'function') {
            try {
                return toSerializable(value.toJSON(), seen);
            } catch (_) {
                // Fall through to manual serialization.
            }
        }

        const out = {};
        Object.keys(value).forEach((key) => {
            out[key] = toSerializable(value[key], seen);
        });
        return out;
    }

    return String(value);
}

export function buildVersionStoragePath(reportId, version) {
    return `documents/${reportId}/versions/ver_${version}.bin`;
}

export function decodeYDocToProsemirror(ydoc) {
    const fragmentNames = ['prosemirror', 'default', 'content', 'doc'];

    for (const name of fragmentNames) {
        try {
            const xmlFragment = ydoc.getXmlFragment(name);
            if (xmlFragment && xmlFragment.length > 0) {
                const json = yDocToProsemirrorJSON(ydoc, name);
                injectMapSnapshotThumbUrls(json, ydoc);
                return json;
            }
        } catch (_) {
            // Continue trying other known fragment names.
        }
    }

    const sharedTypes = Array.from(ydoc.share.keys());
    for (const typeName of sharedTypes) {
        try {
            const sharedType = ydoc.get(typeName);
            if (sharedType && sharedType.constructor?.name === 'YXmlFragment') {
                const json = yDocToProsemirrorJSON(ydoc, typeName);
                injectMapSnapshotThumbUrls(json, ydoc);
                return json;
            }
        } catch (_) {
            // Keep scanning available shared types.
        }
    }

    throw new Error('Could not find ProseMirror content in Yjs document');
}

/**
 * Ticket 06: copy each map-node's currently-persisted preview snapshot (Yjs `mapPreviews` Y.Map,
 * written by the live MapPreviewQueueService while a collaborator has the report open) into that
 * node's `snapshotThumbUrl` attr, in-place on the decoded ProseMirror JSON.
 *
 * Static/read-only renders (e.g. ReadonlyProseMirrorRenderer, used for print/export and
 * non-collaborative preview) have no live Yjs doc to read `mapPreviews` from directly — they only
 * ever see the serialized PM JSON — so without this the map node's `attrs.snapshotThumbUrl` would
 * stay at its schema default (null) and every static render would show the "unavailable"
 * placeholder even when a perfectly good preview exists in the doc.
 *
 * Mutates `node` in place; safe no-op when there's no `mapPreviews` Y.Map or no matching entry
 * (map nodes keep their default null `snapshotThumbUrl`, same as before this ticket).
 */
function injectMapSnapshotThumbUrls(node, ydoc) {
    if (!node || typeof node !== 'object') return;

    let yMapPreviews = null;
    try {
        yMapPreviews = ydoc?.getMap?.('mapPreviews') ?? null;
    } catch (_) {
        yMapPreviews = null;
    }

    const visit = (n) => {
        if (!n || typeof n !== 'object') return;
        if (n.type === 'map' && n.attrs?.nodeId) {
            const preview = yMapPreviews ? getMapPreview(yMapPreviews, n.attrs.nodeId) : '';
            if (preview) n.attrs.snapshotThumbUrl = preview;
        }
        if (Array.isArray(n.content)) n.content.forEach(visit);
    };
    visit(node);
}

export function extractMetadataFromYDoc(ydoc) {
    const metadata = {};
    try {
        const metaMap = ydoc.getMap('documentMeta');
        if (!metaMap) return metadata;
        metaMap.forEach((value, key) => {
            metadata[key] = toSerializable(value);
        });
    } catch (_) {
        // Metadata is optional.
    }
    return metadata;
}

export function extractReferencesFromYDoc(ydoc) {
    const references = [];
    const yReferenceMarks = ydoc.getMap('referenceMarks');
    if (!yReferenceMarks) return references;

    yReferenceMarks.forEach((markMap, referenceMarkId) => {
        const addedRefs = markMap?.get?.('addedReferences');
        if (!addedRefs) return;

        addedRefs.forEach((refMap) => {
            if (!refMap) return;
            const ref = {};
            refMap.forEach((value, key) => {
                ref[key] = toSerializable(value);
            });
            ref.referenceMarkId = ref.referenceMarkId || referenceMarkId;

            ['start', 'end', 'evidenceScore'].forEach((field) => {
                if (typeof ref[field] === 'string') {
                    try {
                        ref[field] = JSON.parse(ref[field]);
                    } catch (_) {
                        // Keep original value when not JSON.
                    }
                }
            });

            if (typeof ref.evidenceScore === 'number') {
                ref.evidenceScore = {
                    score: ref.evidenceScore,
                    reason: ref.evidenceScoreReason ?? null,
                };
            }

            references.push(ref);
        });
    });

    references.sort((a, b) => (a.createdAt ?? 0) - (b.createdAt ?? 0));
    return references;
}

/**
 * Decode a raw Yjs binary buffer to ProseMirror JSON with references and metadata.
 * Used when raw Yjs bytes are available in memory (e.g. from y-websocket).
 * For server-persisted versions use downloadReportVersionFromApi instead.
 */
export function decodeYDocBytesToProsemirror(bytes, reportId, version) {
    const ydoc = new Y.Doc();
    Y.applyUpdate(ydoc, new Uint8Array(bytes));

    const prosemirrorJSON = decodeYDocToProsemirror(ydoc);
    const references = extractReferencesFromYDoc(ydoc);
    const metadata = extractMetadataFromYDoc(ydoc);

    return {
        ...prosemirrorJSON,
        references,
        metadata,
        version,
        reportId,
    };
}


